import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import TelegramIcon from "@mui/icons-material/Telegram"
import {
    DialogTitle,
    DialogContent,
    Stack,
    DialogActions,
    DialogContentText,
    Box,
    useTheme,
    Collapse,
    AlertTitle,
} from "@mui/material"
import { Dispatch, ReactElement, SetStateAction } from "react"
import { useForm } from "react-hook-form"

import { EMAIL_REGEX } from "../../../constants/patterns"
import useInviteUser from "../../../services/middleware/hooks/account/useInviteUser"
import { GRID_SPACING } from "../../../themes/defaultThemeParams"
import AppButton from "../../buttons/appButton"
import AppLoadingButton from "../../buttons/appLoadingButton"
import AppAlert from "../../dataDisplay/alerts/appAlert"
import AppDialog from "../../dialog/appDialog"
import ValidatedTextField from "../../inputs/validated/validatedTextField"
import { AppCardHeader } from "../../surfaces/appCard"

const INVITEE_REG = "inviteEmail"

interface IInviteUser {
    isOpen: boolean
    setIsOpen: Dispatch<SetStateAction<boolean>>
}

/**
 * Dialog where admins can raise alerts on TTR devices.
 * This is an uncontrolled form, registered with react-hook-form.
 * If a component is mounted, it is automatically registered with react-hook-form.
 */
const InviteUser = ({ isOpen, setIsOpen }: IInviteUser): ReactElement => {
    const theme = useTheme()
    const { control, handleSubmit, getValues } = useForm({
        shouldUnregister: true,
    })
    const { error, isSending, postInvite } = useInviteUser(setIsOpen)

    const submitResponse = (): void => {
        const email = getValues(INVITEE_REG)
        postInvite(email)
    }

    return (
        <AppDialog isOpen={isOpen}>
            <DialogTitle>
                <AppCardHeader
                    sx={{ p: 0 }}
                    titleTypographyProps={{
                        variant: "h5",
                        fontWeight: "500",
                    }}
                    title={
                        <>
                            <Box
                                color={theme.palette.secondary.main}
                                display="inline"
                            >
                                Invite Someone
                            </Box>{" "}
                            to join your team
                        </>
                    }
                    subheader="Use this form to invite a new user to join your team"
                />
            </DialogTitle>
            <DialogContent>
                <Stack spacing={GRID_SPACING} paddingTop="8px">
                    <DialogContentText>
                        Need to add someone to your team? Just enter their email
                        address below and we&apos;ll send them an invite. They
                        don&apos;t need to have an account yet. We&apos;ll check
                        to see, and if they don&apos;t have one, we&apos;ll set
                        one up for them. Your invitee will be given the option
                        to accept or decline your invite when they next log in.
                    </DialogContentText>
                    <Collapse in={!!error} mountOnEnter>
                        <AppAlert severity={error?.level}>
                            <AlertTitle>{error?.message}</AlertTitle>
                            {error?.displayDetail}
                        </AppAlert>
                    </Collapse>
                    <ValidatedTextField
                        control={control}
                        required
                        registerName={INVITEE_REG}
                        label="Email Address"
                        disabled={isSending}
                        handleKeyPress={(e) => {
                            if (e.key === "Enter" && !e.altKey) {
                                e.preventDefault()
                                handleSubmit(submitResponse)()
                            }
                        }}
                        placeholder="Enter email address of the person you wish to invite..."
                        multiline
                        customRules={{
                            pattern: {
                                value: EMAIL_REGEX,
                                message: "Please enter a valid email address",
                            },
                        }}
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <AppButton
                    startIcon={<ArrowBackIcon />}
                    variant="outlined"
                    onClick={() => setIsOpen(false)}
                >
                    Back
                </AppButton>
                <AppLoadingButton
                    color="success"
                    variant="contained"
                    endIcon={<TelegramIcon />}
                    loading={isSending}
                    onClick={handleSubmit(submitResponse)}
                >
                    Invite
                </AppLoadingButton>
            </DialogActions>
        </AppDialog>
    )
}

export default InviteUser
